import { useCallback, useEffect, useRef, useState } from 'react';
import { commsApi } from '@/utils/commsApi';

const DEFAULTS = { enabled: false, start: '22:00', end: '07:00', tz: 'UTC' };
const toMinutes = (hm) => Number(hm.slice(0, 2)) * 60 + Number(hm.slice(3, 5));

// Current HH:MM in the time zone the setting was saved in.
const nowMinutesIn = (tz) => {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(new Date());
    const h = Number(parts.find((p) => p.type === 'hour')?.value ?? 0) % 24;
    const m = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
    return h * 60 + m;
  } catch {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  }
};

export const isQuietAt = (qh) => {
  if (!qh?.enabled) return false;
  const now = nowMinutesIn(qh.tz || 'UTC');
  const start = toMinutes(qh.start);
  const end = toMinutes(qh.end);
  if (start === end) return true;
  return start < end ? now >= start && now < end : now >= start || now < end;
};

export const deviceTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

// Account-level quiet hours (shared across devices); re-evaluated every 30 s.
export function useQuietHours() {
  const [quietHours, setQuietHours] = useState(DEFAULTS);
  const [quietActive, setQuietActive] = useState(false);
  const ref = useRef(DEFAULTS);

  useEffect(() => {
    commsApi.quietHours().then((qh) => { ref.current = qh; setQuietHours(qh); setQuietActive(isQuietAt(qh)); }).catch(() => {});
  }, []);

  useEffect(() => {
    const tick = () => setQuietActive(isQuietAt(ref.current));
    tick();
    const timer = setInterval(tick, 30000);
    return () => clearInterval(timer);
  }, [quietHours]);

  const saveQuietHours = useCallback(async (next) => {
    const saved = await commsApi.saveQuietHours({ ...next, tz: deviceTimeZone() });
    ref.current = saved;
    setQuietHours(saved);
    setQuietActive(isQuietAt(saved));
    return saved;
  }, []);

  const isQuietNow = useCallback(() => isQuietAt(ref.current), []);

  return { quietHours, quietActive, saveQuietHours, isQuietNow };
}
